export type ProjectArchiveScope = {
    projectIds: Set<string>;
    siteIds: Set<string>;
    buildingIds: Set<string>;
    floorIds: Set<string>;
    siteAddressKeys: Set<string>;
};

function entityIdAsString(id: any): string | undefined {
    if (id == null || id === '') return undefined;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id !== null && '$oid' in id) return (id as { $oid: string }).$oid;
    if (typeof id === 'object' && id !== null && '_id' in id) return entityIdAsString((id as { _id: unknown })._id);
    return String(id);
}

function siteAddressKey(value: any): string | undefined {
    const street = typeof value?.street === 'string' ? value.street.trim() : '';
    const streetNumber = typeof value?.streetNumber === 'string' ? value.streetNumber.trim() : '';
    const postalCode = typeof value?.postalCode === 'string' ? value.postalCode.trim() : '';

    return street && streetNumber && postalCode
        ? JSON.stringify([street, streetNumber, postalCode])
        : undefined;
}

export function projectArchiveSiteAddressKeys(sites: any[]): Set<string> {
    return new Set(
        (sites || [])
            .map(siteAddressKey)
            .filter((key: string | undefined): key is string => typeof key === 'string' && key.length > 0)
    );
}

function collectEntityReferenceIds(...values: unknown[]): Set<string> {
    const ids = new Set<string>();
    const visit = (value: unknown) => {
        if (value == null || value === '') {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }
        const id = entityIdAsString(value);
        if (id) {
            ids.add(id);
        }
    };
    values.forEach(visit);
    return ids;
}

function hasAnyId(ids: Set<string>, allowedIds: Set<string>) {
    return Array.from(ids).some((id) => allowedIds.has(id));
}

export function assetMatchesProjectArchiveScope(asset: any, scope: ProjectArchiveScope) {
    const addressKey = siteAddressKey(asset);
    const assetProjectIds = collectEntityReferenceIds(asset.project, asset.projectId, asset.projects, asset.projectIds, asset._project);
    const matchesProject = hasAnyId(assetProjectIds, scope.projectIds);

    if (assetProjectIds.size > 0 && scope.projectIds.size > 0 && !matchesProject) {
        return false;
    }

    return (
        matchesProject ||
        hasAnyId(collectEntityReferenceIds(asset.site, asset.siteId, asset.sites, asset.siteIds, asset._site), scope.siteIds) ||
        hasAnyId(collectEntityReferenceIds(asset.building, asset.buildingId, asset.buildings, asset.buildingIds, asset._building), scope.buildingIds) ||
        hasAnyId(collectEntityReferenceIds(asset.floor, asset.floorId, asset.floors, asset.floorIds, asset._floor), scope.floorIds) ||
        (typeof addressKey === 'string' && scope.siteAddressKeys.has(addressKey))
    );
}
